// Bill of Quantities — simplified.
// Each row captures a UNIT (a single bay / a single truck / a single mezzanine)
// with only three dimensions: Height × Depth × Length (mm).
// Rows with identical (type, H, D, L) collapse into a single line with a
// summed quantity — where "quantity" is the number of bays (for racks) or the
// number of units (for MHE, mezzanines, obstacles).
//
// This module powers both the CSV download and the PDF BoQ table.

import { OBJECT_DEFS, objectFootprint } from "../objectDefs";

/**
 * Return the per-bay/unit dimensions and the count of bays/units contributed
 * by a single object.
 *
 *   H — vertical (height)          in mm
 *   D — depth (perpendicular)      in mm
 *   L — length / bay-length        in mm
 *   qty — number of bays / units this object contributes
 */
function dimensionsOf(obj) {
  const p = obj.props || {};
  switch (obj.type) {
    case "pallet_rack":
      return {
        H: p.height || 0,
        D: p.depth || 0,
        L: p.bay_length || 0,
        qty: (p.num_bays || 0) * (p.back_to_back ? 2 : 1),
      };
    case "drive_in_rack":
      return {
        H: p.height || 0,
        D: p.pallet_depth || 0,
        L: p.lane_width || 0,
        qty: (p.lanes || 0) * (p.depth_pallets || 0),
      };
    case "cantilever_rack":
      return {
        H: p.height || 0,
        D: p.arm_length || 0,
        L: p.column_spacing || 0,
        qty: (p.columns || 0) * (p.double_sided ? 2 : 1),
      };
    case "slotted_angle_rack":
      return {
        H: p.height || 0,
        D: p.depth || 0,
        L: p.width || 0,
        qty: (p.units_in_row || 1) * (p.double_sided ? 2 : 1),
      };
    case "wall_rack":
      return {
        H: p.height || 0,
        D: p.depth || 0,
        L: p.width || 0,
        qty: p.bays || 1,
      };
    case "gondola_rack":
      return {
        H: p.height || 0,
        D: p.depth || 0,
        L: p.width || 0,
        qty: (p.bays || 1) * (p.double_sided ? 2 : 1),
      };
    case "corner_rack":
      return {
        H: p.height || 0,
        D: p.depth || 0,
        L: Math.max(p.leg_a || 0, p.leg_b || 0),
        qty: 1,
      };
    case "pegboard_display":
      return { H: p.height || 0, D: p.depth || 0, L: p.width || 0, qty: 1 };
    case "mezzanine":
      return {
        H: p.floor_height || 0,
        D: p.width || 0,
        L: p.length || 0,
        qty: 1,
      };
    default: {
      // Everything else (MHE, obstacles, pallets, freezers, checkouts, etc.)
      // has explicit length/width/height in mm.
      return {
        H: p.height || 0,
        D: p.width || 0,
        L: p.length || 0,
        qty: 1,
      };
    }
  }
}

/**
 * Group all objects into unique (type × H × D × L) rows. Each row's `qty` is
 * the total number of bays / units across the whole project.
 */
export function computeBOM(state) {
  const groups = new Map();
  for (const obj of state.objects || []) {
    const def = OBJECT_DEFS[obj.type];
    if (!def) continue;
    const { H, D, L, qty } = dimensionsOf(obj);
    if (qty <= 0) continue;
    const key = `${obj.type}::${H}x${D}x${L}`;
    if (!groups.has(key)) {
      groups.set(key, {
        type: obj.type,
        category: def.category,
        label: def.label,
        H, D, L,
        dimStr: `${H} × ${D} × ${L} mm`,
        qty: 0,
      });
    }
    groups.get(key).qty += qty;
  }
  // Sort: category order, then item label, then largest by height first.
  const catOrder = ["racks", "mezzanine", "super_racks", "super_cold", "super_pos", "super_misc", "mhe", "obstacles"];
  return Array.from(groups.values()).sort((a, b) => {
    const ai = catOrder.indexOf(a.category);
    const bi = catOrder.indexOf(b.category);
    if (ai !== bi) return ai - bi;
    if (a.label !== b.label) return a.label.localeCompare(b.label);
    return b.H - a.H;
  });
}

/**
 * Normalise both persisted server payloads (name, client_name, floor_plan, …)
 * and the raw client store into a single shape used by the exporters.
 */
export function normaliseState(state) {
  return {
    projectName: state.projectName ?? state.name ?? "Untitled project",
    clientName:  state.clientName  ?? state.client_name ?? "",
    projectType: state.projectType ?? state.project_type ?? "warehouse",
    units:       state.units       ?? "mm",
    ceilingHeight: state.ceilingHeight ?? state.ceiling_height ?? 0,
    floor:       state.floor       ?? state.floor_plan ?? { vertices: [], doors: [] },
    objects:     state.objects     ?? [],
    compliance:  state.compliance  ?? {},
  };
}

/**
 * Aggregate high-level project totals from the BoQ rows for the cover page.
 */
export function computeTotals(rows, state) {
  const s = normaliseState(state);
  const totals = {
    totalUnits: 0,
    rackBays: 0,
  };
  for (const r of rows) {
    totals.totalUnits += r.qty;
    if (["racks", "super_racks"].includes(r.category)) totals.rackBays += r.qty;
  }
  // Floor area from polygon (shoelace formula).
  const verts = s.floor?.vertices || [];
  let area = 0;
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i], b = verts[(i + 1) % verts.length];
    area += a.x * b.y - b.x * a.y;
  }
  totals.floorArea_m2 = Math.abs(area) / 2 / 1e6;
  return totals;
}

/**
 * CSV export of the simplified BoQ (Item, Dimensions, Qty).
 */
export function exportBoQCsv(state, filename = "boq.csv") {
  const s = normaliseState(state);
  const rows = computeBOM(s);
  const totals = computeTotals(rows, s);
  const header = ["Item", "Dimensions (H × D × L mm)", "Qty (bays / units)"];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([r.label, `"${r.dimStr}"`, r.qty].join(","));
  }
  lines.push("");
  lines.push(`# Project: ${s.projectName || "-"}`);
  lines.push(`# Client: ${s.clientName || "-"}`);
  lines.push(`# Floor area (m²): ${totals.floorArea_m2.toFixed(2)}`);
  lines.push(`# Total rack bays: ${totals.rackBays}`);
  lines.push(`# Total units (all items): ${totals.totalUnits}`);
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
